import React, { useEffect, useState, useContext } from "react"
import styles from "./index.module.scss"
import XPMotorApi from '@xpeng/xpmotors-jsbridge-api'
import html2canvas from 'html2canvas'
import {
  TripsType,
  PROP_DICT,
  formatTripDuration,
  getDaysApart,
  formatHighlightData
} from '../config'
import {
  getLoginInfoPromise,
  formatTimestampToTxt,
  formatVehiclePlate,
  desensitizedVin,
  disableShareOpt
} from '@/utils/common'
import {
  ErrorPlaceholder,
  EmptyPlaceholder,
} from "@/components/placeholder"
import LoadingPage from "@/components/loading-page"
import LoadingMore from '@/components/loading-more'
import MessageBox from '@/components/message-box'
import useQuery from "@/hooks/useQuery"
import { reportData } from "@/utils/report-data"
import { ThemeModeContext } from "../../../ThemeModeContext"
import { isSupportLocationDisplay } from "../config"

import {
  getTripsDetail,
  getUserVehicleInfo,
  getMileageVehicleInfo
} from '@/api/trips-report'
import './_variables.css'
import IntelligentDriveData from '../components/IntelligentDriveData';

// 设计稿canvas轨迹颜色
const TRIPS_LINE_COLOR = {
  [TripsType.normal]: '#c0c0c0',
  [TripsType.lccOrAcc]: '#89B843',
  [TripsType.ngp]: '#89B843'
}
// 设计稿canvas轨迹宽度
const TRIPS_LINE_WIDTH = 3
// 设计稿canvas画布宽高number(100%=3.75)
const CANVAS_WIDTH = 3.75
// 设计稿canvas icon宽高
const ICON_WIDTH = 50
const ICON_HEIGHT = 50
// canvas icon
const ICON_START_SRC = require('../assets/depart.png')
const ICON_END_SRC = require('../assets/destination.png')
// 解决canvas模糊问题配置的css scale值
const CSS_SCALE = 0.5

const DEFAULT_ERROR_MSG = '服务器开小差啦'

const App = () => {
  const themeModeContext = useContext(ThemeModeContext)
  const themeMode = themeModeContext?.mode || 'light'
  // 判断是否支持位置展示
  const isSupportLocation = isSupportLocationDisplay()
  const vin = useQuery().get('vin') || ''
  const tripId = useQuery().get('tripId') || ''
  const tripType = useQuery().get('type') || ''
  const isSuperErev = useQuery().get('isSuperErev') === 'true'

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingError, setIsLoadingError] = useState(false)
  const [isEmptyPage, setIsEmptyPage] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const [showScreenshot, setShowScreenshot] = useState(false)
  const [tripsDetail, setTripsDetail] = useState<any>({})
  const [loginInfo, setLoginInfo] = useState({
    avatar: '',
    nickname: ''
  })
  const [vehicleInfo, setVehicleInfo] = useState({
    plateNo: '',
    vehicleTypeSaleName: ''
  })
  const [mileageInfo, setMileageInfo] = useState({
    totalOdometer: '',
    totalAdMileage: '',
    ownershipStartTs: 0,
  })
  
  useEffect(() => {
    document.title = '行程记录'
    
    // 进行程页关闭自定义导航栏图标，否则ios导航栏会将图标带进来
    disableShareOpt()

    setIsLoading(true)

    getLoginInfo()
    getUserInfo(vin)
    getMileageInfo(vin)
    getTripsDetailData({ vin, tripId, type: tripType })
  }, [])

  useEffect(() => {
    if (Object.keys(tripsDetail).length) {
      drawTripsLine(tripsDetail?.trackDetail?.trackPoints)
    }
  }, [isLoading, tripsDetail])

  const getLoginInfo = async () => {
    try {
      const data: any = await getLoginInfoPromise()
      setLoginInfo({
        avatar: data.avatar,
        nickname: data.nickname
      })
    } catch (error) {}
  }

  const getUserInfo = async (vin) => {
    try {
      const res: any = await getUserVehicleInfo({ vin })
      if (res.code === 200) {
        const data = res.data
        if (Array.isArray(data) && data.length > 0) {
          setVehicleInfo({
            plateNo: data[0]?.plateNo,
            vehicleTypeSaleName: data[0]?.vehicleTypeSaleName,
          })
        } else {
          // 当前用户是该车的授权使用人，data数组为空
          setIsLoading(false)
          setErrorMsg('暂不支持该功能')
          setIsEmptyPage(true)
        }
      } else {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsEmptyPage(true)
      }
    } catch (error) {
      setErrorMsg(DEFAULT_ERROR_MSG)
      setIsLoadingError(true)
    }
  }

  const getMileageInfo = async (vin) => {
    try {
      const res: any = await getMileageVehicleInfo({ vin })
      if (res.code === 200) {
        setMileageInfo(res.data)
      } else {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsEmptyPage(true)
      }
    } catch (error) {
      setErrorMsg(DEFAULT_ERROR_MSG)
      setIsLoadingError(true)
    }
  }

  const getTripsDetailData = async (params) => {
    try {
      const res: any = await getTripsDetail(params)
      if (res.code === 200) {
        setTripsDetail(formatTripDeteilData(res.data))
      } else {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsEmptyPage(true)
      }
    } catch (error) {
      setErrorMsg(DEFAULT_ERROR_MSG)
      setIsLoadingError(true)
    } finally {
      setIsLoading(false)
    }
  }
  const formatTripDeteilData = (data) => {
    // console.log('intelligentDriveDataArr', formatIntelligentDriveData(data.intelligentDriveData));
    // const ngpLccMileage = [
    //   { label: PROP_DICT['ngpMileage'], value: data?.ngpMileage, rate: data.ngpMileageRate },
    //   { label: PROP_DICT['accLccMileage'], value: data?.accLccMileage, rate: data.accLccMileageRate }
    // ]
    // console.log(formatHighlightData(data.intelligentDriveData, 6));
    return {
      ...data,
      hasLocation: !!data.startLocation && !!data.endLocation && isSupportLocation,
      tripDuration: formatTripDuration(data.tripDuration),
      startTime: formatTimestampToTxt(data.startTs, 'HH:mm'),
      endTime: formatTimestampToTxt(data.endTs, 'HH:mm'),
      gapDays: getDaysApart(data.startTs, data.endTs),
      zeroTakeOverFlag: data?.intelligentDriveData?.zeroTakeOverFlag,
      intelligentDriveDataArr: [
        ...formatHighlightData(data.intelligentDriveData, 6)
      ]
    }
  }

  // const formatIntelligentDriveData = (intelligentDriveData) => {
  //   const intelligentDriveDataArr: { label: string, value: number, rate?: number }[] = []
  //   const intelligentKeyList: string[] = Array.from(INTELLIGENT_DRIVE_DATA.keys())
  //   intelligentKeyList.forEach((key: string) => {
  //     const value = formatValue(intelligentDriveData?.[key])
  //     value && intelligentDriveDataArr.push({
  //       label: `${INTELLIGENT_DRIVE_DATA.get(key)?.label}(${INTELLIGENT_DRIVE_DATA.get(key)?.unit})`,
  //       value
  //     })
  //   })
  //   // 如果大于3条数据，则只保留前3条数据
  //   intelligentDriveDataArr.length > 3  && intelligentDriveDataArr.splice(3)
  //   return intelligentDriveDataArr
  // }

  // function formatValue(input: any): number {
  //   // 尝试转换为数字
  //   const num = Number(input);
    
  //   // 检查是否为有效数字（非 NaN）
  //   if (isNaN(num)) {
  //     return 0
  //   }
    
  //   // 处理负数（包括负整数和负浮点数）
  //   if (num < 0) {
  //     return 0
  //   }
    
  //   // 处理正整数
  //   if (Number.isInteger(num)) {
  //     return num
  //   }
    
  //   // 处理正浮点数（保留一位小数，四舍五入）
  //   return parseFloat(num.toFixed(1))
  // }

  // const defineSelfShareAction = (callback) => {
  //   JSBridge.registerHandler('onActionButtonShowing', (data, responseCallback) => {
  //     // true = 显示, false = 隐藏
  //     const options = {
  //       share: true
  //     }
  //     responseCallback(options)
  //   })
  //   JSBridge.registerHandler('onActionButtonClick', (data, responseCallback) => {
  //     const res = data instanceof Array ? data[0] : data;
  //     if (res.action === 'share') {
  //       // 通知原生端将要拦截该事件
  //       responseCallback({ preventDefault: true })
  //       // 触发自定义的事件
  //       callback()
  //     }
  //   })
  // }

  /**
   * 画行程轨迹
   * @param points 
   */
  const drawTripsLine = (points) => {
    // 处理经纬度数据
    points = formatPoints(points)

    const canvas = document.getElementById('canvas') as HTMLCanvasElement

    if (canvas && points?.length) {
      // 移动端canvas不支持rem，这里转换为px(1px=0.01rem 以设计稿宽度375px为准)
      const currentFontSize = document.body.clientWidth * 100 / 375
      console.log('currentFontSize', currentFontSize)

      // #root设置了最大宽度为750px
      const canvasWidthAndHeight = CANVAS_WIDTH * currentFontSize > 750 ? 750 : CANVAS_WIDTH * currentFontSize
      canvas.setAttribute('width', `${canvasWidthAndHeight}px`)
      canvas.setAttribute('height', `${canvasWidthAndHeight}px`)

      console.log('canvas width and height -->', canvas.width, canvas.height)

      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

      ctx.strokeStyle = TRIPS_LINE_COLOR[TripsType.normal];
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // 将canvas画出的行程轨迹在画布居中，取x、y的中位数(缩放后)，再减去画布宽高的一半(缩放后)，做位移
      const allX = points.map(item => item.x)
      const maxX = Math.max(...allX)
      const minX = Math.min(...allX)
      const middleX = (maxX + minX) / 2

      const allY = points.map(item => item.y)
      const maxY = Math.max(...allY)
      const minY = Math.min(...allY)
      const middleY = (maxY + minY) / 2
      
      console.log('points -->', points)
      console.log('maxX minX maxY minY "maxX-minX" "maxY-minY" -->', maxX, minX, maxY, minY, maxX-minX, maxY-minY)
      console.log('middleX middleY -->', middleX, middleY)

      // 根据整体轨迹图案宽高的最大值，来计算相对canvas画布大小需要缩放的倍数
      let scaleNum = 1
      if (maxX-minX > maxY-minY) {
        scaleNum = canvas.width / (maxX-minX)
      } else {
        scaleNum = canvas.height / (maxY-minY)
      }

      // 倍数减少1/7以防止icon被边界截断
      scaleNum = scaleNum - scaleNum / 7
      
      console.log('scaleNum --> ', scaleNum)
      // 缩放适应轨迹宽高
      ctx.scale(scaleNum, scaleNum)
      // 画布整体缩放，解决模糊问题，画布相对页面进行排版的时候需要特殊处理
      canvas.setAttribute('style', `transform:scale(${CSS_SCALE})`)

      // 缩放后再定义线宽
      const scaleLineWidth = TRIPS_LINE_WIDTH / scaleNum / CSS_SCALE
      ctx.lineWidth = scaleLineWidth

      // 缩放后icon大小跟随变化，要随之恢复
      const scaleIconWidth = ICON_WIDTH / scaleNum / CSS_SCALE
      const scaleIconHeight = ICON_HEIGHT / scaleNum / CSS_SCALE

      // 将canvas实例位移至画布中心
      ctx.translate(-middleX + canvas.width / 2 / scaleNum, -middleY + canvas.height / 2 / scaleNum)

      // 将非自驾和自驾的片段分成不同数组，用于分段画图
      const segmentedArray: any[] = []
      points.forEach((item, index) => {
        if (index === 0) {
          segmentedArray.push([item])
        } else {
          if (item.a !== points[index - 1].a) {
            segmentedArray.push([item])
          } else {
            segmentedArray[segmentedArray.length - 1].push(item)
          }
        }
      })

      console.log('segmentedArray --> ', segmentedArray)

      // 分段画轨迹
      if (segmentedArray.length) {
        segmentedArray.forEach((item, index) => {
          ctx.beginPath()

          // 判断当前分段是哪种类型，配对应颜色
          if (typeof (item[0].a) === 'number') {
            ctx.strokeStyle = TRIPS_LINE_COLOR[item[0].a]
          }

          // 分段以后ctx绘制起点要移动到上一段的末尾点，防止绘制轨迹断开
          if (index > 0) {
            const lastItem = segmentedArray[index - 1]
            console.log('分段连接处 --> ', index, lastItem[lastItem.length - 1].x, item[0].x)
            ctx.moveTo(lastItem[lastItem.length - 1].x, lastItem[lastItem.length - 1].y)
          }

          item?.forEach((subItem, subIndex) => {
            // 画起点icon
            if (index === 0 && subIndex === 0) {
              const iconStart = new Image()
              iconStart.src = ICON_START_SRC
              iconStart.onload = function () {
                // ctx.drawImage(iconStart, subItem.x - scaleIconWidth / 2, subItem.y - scaleIconHeight / 2, scaleIconWidth, scaleIconHeight)
                ctx.drawImage(iconStart, subItem.x - scaleIconWidth / 2 - scaleLineWidth / 4, subItem.y - scaleIconHeight / 2 - scaleLineWidth, scaleIconWidth, scaleIconHeight)
              }
            }
            // 画终点icon
            if (index === segmentedArray.length - 1 && subIndex === item.length - 1) {
              const iconEnd = new Image()
              iconEnd.src = ICON_END_SRC
              iconEnd.onload = function () {
                // ctx.drawImage(iconEnd, subItem.x - scaleIconWidth / 2, subItem.y - scaleIconHeight / 2, scaleIconWidth, scaleIconHeight)
                ctx.drawImage(iconEnd, subItem.x - scaleIconWidth / 2 - scaleLineWidth / 4, subItem.y - scaleIconHeight / 2 - scaleLineWidth, scaleIconWidth, scaleIconHeight)
              }
            }
            ctx.lineTo(subItem.x, subItem.y)
          })

          ctx.stroke()
        })
      }
    }
  }

  /**
   * x,y对应经度、纬度（y取反，纬度增加位移向上），服务端返回100个点
   * 这里因为经纬度变化都集中在小数点第三位以后，所以先移动小数点到第六位，再缩小取到整数，此时变化幅度较为合适
   * 数据不足6位小数的 补足6位
   * @param points
   * @returns 
   */
  const formatPoints = (points: { x: any, y: any, a: any }[]) => {
    const renderPoints = !!points?.length && points.map(item => {
      item.x = Number(item.x).toFixed(6)
      item.y = Number(item.y).toFixed(6)
      return {
        ...item,
        x: Number((+((item.x * 1000000).toString().slice(1)) / 5000).toFixed(2)), // 精度的变化主要集中在后几位，把几乎不变的第一位排除在外，再缩小取整，保持变化幅度合适
        y: -Number((+((item.y * 1000000).toString()) / 5000).toFixed(2))
      }
    })
    console.log('renderPoints --> ', renderPoints)
    return renderPoints
  }

  const drawShareImage = () => {
    return new Promise<any>((resolve, reject) => {
      const backgroundColor = themeMode === 'light' ? 'null' : '#000'
      html2canvas(document.querySelector('#share-area') as HTMLElement, {
        useCORS: true, // 允许画布图片跨域
        // left 0 与 top 0处开始绘制，避免滚动过程中影响绘制
        x: 0,
        y: 0,
        // 透明背景fix右边、下边有白边问题
        // backgroundColor: null, 
        // 背景色设置为透明/黑色，避免透明背景时右边、下边有白边问题
        backgroundColor: backgroundColor,
        // window.devicePixelRatio 设备像素比 这里设置scale尽量保证图片质量
        scale: window?.devicePixelRatio || 2,
        // 移除元素
        ignoreElements: element => {
          if (element.id === 'help-icon' || element.id === 'share-icon') {
            return true;
          }
          return false;
        }
      })
        .then(canvas => {
          // console.log('canvas.toDataURL -->', canvas.toDataURL('image/png'))
          resolve(canvas.toDataURL('image/png'))
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  const callShare = () => {
    // 分享点击埋点
    reportData({pageId: 'P11181', buttonId: 'B003', vin: vin, params: {
      carType: vehicleInfo.vehicleTypeSaleName,
      tripTime: tripsDetail.startTs,
      smartDrivePer: tripsDetail.adMileageRate + '%'
    }})

    setShowScreenshot(true)
    setTimeout(async () => {
      try {
        const imgData = await drawShareImage()
        XPMotorApi.sharePanel({
          style: imgData ? 4 : 1,
          addExtInfo: false, 
          addUserInfo: false,
          addQrCode: false,
          data: {
            title: '',
            content: '',
            postImage: imgData ? imgData.replace('data:image/png;base64,', '') : '',
            url: encodeURIComponent(window.location.href),
            shareType: 2
          }
        } as any).then(res => {
          console.log('XPMotorApi.sharePanel --> ', res)
        }).catch(() => {})
      } catch (error) {
        console.log('callShare error -->', error)
      } finally {
        setShowScreenshot(false)
      }
    }, 0)
  }

  const getTimeTxt = (ts: number, type = 'date') => {
    if (!!ts) {
      // 转为毫秒级时间戳才能获得准确数据
      if (ts.toString().length === 10) {
        ts = ts * 1000
      }
      if (type === 'date') {
        return formatTimestampToTxt(ts, 'YYYY/MM/DD')
      }
      if (type === 'day') {
        const day = new Date(ts).getDay()
        const WEEK_LIST = ['一', '二', '三', '四', '五', '六', '日']
        if (day === 0) return '周日'
        return `周${WEEK_LIST[day - 1]}`
      }
      if (type === 'time') {
        return formatTimestampToTxt(ts, 'HH:mm')
      }
    } else {
      return '--'
    }
  }

  const showMessageBox = (title: string, children: any, confirmButtonText = '知道了') => {
    MessageBox.alert({ title, confirmButtonText, children })
  }

  // const showNgpMileageTips = () => {
  //   showMessageBox('智驾里程', <div>1. 指行驶中使用了任意智驾功能的里程数，包括使用导航辅助驾驶或ACC/LCC功能。<br/>2. 百分比为占行驶里程的比例。</div>)
  //   // 智驾里程问号点击埋点
  //   reportData({pageId: 'P11181', buttonId: 'B001', vin: vin, params: {
  //     carType: vehicleTypeSaleName
  //   }})
  // }

  const showNgpUseTips = () => {
    showMessageBox('辅助驾驶数据', <div>统计期间未使用的辅助驾驶功能不作显示。</div>)
    // 智驾数据问号点击埋点
    reportData({pageId: 'P11181', buttonId: 'B002', vin: vin, params: {
      carType: vehicleInfo.vehicleTypeSaleName
    }})
  }

  // const showAdMileageTips = () => {
  //   showMessageBox('导航辅助驾驶', <div>1. 导航辅助驾驶包含高速NGP和城市NGP。<br/>2. 百分比为占行驶里程的比例。</div>)
  // }

  // const showAccLccTips = () => {
  //   showMessageBox('ACC/LCC', <div>1. 开启导航辅助驾驶功能时，ACC/LCC功能也会同步开启。<br/>2. 百分比为占行驶里程的比例。</div>)
  // }

  if (isLoading)
    return (
      <LoadingPage />
    )

  if (isLoadingError)
    return (
      <ErrorPlaceholder
        message={errorMsg}
      ></ErrorPlaceholder>
    )

  if (isEmptyPage)
    return (
      <EmptyPlaceholder text={errorMsg}></EmptyPlaceholder>
    )

  // const hideAdData = !+tripsDetail.ngpMileage && !+tripsDetail.accLccMileage
  const hideAdData = !+tripsDetail.adMileage && tripsDetail?.intelligentDriveDataArr?.length === 0
  const daysToBeCarOwner = ((new Date().getTime() - mileageInfo.ownershipStartTs) / 1000 / 3600 / 24).toFixed(0)

  return (
    <div className={styles.homePage}>
      {
        showScreenshot && 
        <div className={styles.shareModal}>
          <LoadingMore/>
        </div>
      }
      <div className={styles.shareBtn} id='share-icon' onClick={callShare}>
        <img src={require('../assets/share.png')} alt='' className={styles.shareBtnImg}></img>
        <span className={styles.shareText}>分享</span>
      </div>
      <div className={`${styles.shareArea} ${showScreenshot ? styles.shareBg : ''} `} id='share-area'>
        {
          showScreenshot && (
            <>
              {/* <img className={styles.shareBgImg} alt='' src={require('../assets/bg_share_2.png')}></img> */}
              {/* <img className={styles.logoImg} alt='' src={require('../assets/logo.png')}></img> */}
              <div className={styles.shareBgImg}></div>
              <div className={styles.logoImg}></div>
            </>
          )
        }
        <div className={`${styles.tripsDetail} ${showScreenshot ? styles.paddingLogoTop : ''}` }>
          <div className={styles.dataArea}>
            <div className={styles.time}>{getTimeTxt(tripsDetail.startTs, 'date')} {getTimeTxt(tripsDetail.startTs, 'day')} {getTimeTxt(tripsDetail.startTs, 'time')}</div>
            <div className={styles.vehicleInfo}>
              {vehicleInfo.vehicleTypeSaleName} {vehicleInfo.plateNo ? formatVehiclePlate(vehicleInfo.plateNo) : desensitizedVin(vin)}
            </div>
            <div className={styles.datas}>
              <div className={styles.dataBlock}>
                <div className={styles.data}>{tripsDetail.driveMileage}</div>
                <div className={styles.name}>{PROP_DICT['driveMileage']}</div>
              </div>
              {
                !!+tripsDetail.adMileage && (
                  <div className={styles.dataBlock}>
                    <div className={styles.adData}>
                      <div className={styles.data}>{tripsDetail.adMileage}</div>
                      <div className={styles.rate}>{+tripsDetail.adMileageRate === 0 ? '<1' : tripsDetail.adMileageRate}%</div>
                    </div>
                    <div className={styles.adName}>
                      <div className={styles.name}>{PROP_DICT['adMileage']}</div>
                    </div>
                  </div>
                )
              }
              <div className={styles.dataBlock}>
                <div className={styles.tripDurationContainer}>
                  <div className={styles.time}>{tripsDetail.tripDuration?.time}</div>
                  {/* <div className={styles.unit}>{tripsDetail.tripDuration?.unit}</div> */}
                </div>
                <div className={styles.name}>{`${PROP_DICT['tripDuration']}(${tripsDetail.tripDuration?.unit})`}</div>
              </div>
            </div>
          </div>
          <div className={styles.trackArea}>
            <div className={styles.canvasContainer}>
              <canvas id="canvas"></canvas>
            </div>
            {
              !!+tripsDetail.adMileage && (
                <div className={styles.labels}>
                  <div className={styles.adLabel}>
                    <div className={styles.adIcon}></div>
                    <div className={styles.text}>辅助驾驶</div>
                  </div>
                </div>
              )
            }
          </div>
        </div>
        { tripsDetail.hasLocation && 
          <div className={styles.locationBar}>
            <div className={styles.locationContainer}>
              <div className={`${styles.iconContainer}`}>
                <div className={`${styles.bigCircle} ${styles.start}`}></div>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div className={styles.dashCircle} key={index}></div>
                  ))}
                <div className={`${styles.bigCircle} ${styles.end}`}></div>
              </div>
              <div className={styles.locationContent}>
                <div className={styles.locationContnentText}>
                  {tripsDetail.startLocation}
                </div>
                <div className={styles.locationContnentText}>
                  {tripsDetail.endLocation}
                </div>
              </div>
            </div>
          </div>
        }
        {
          !hideAdData && (
            <div className={styles.driveData}>
              <div className={styles.title} onClick={showNgpUseTips}>
                <div className={styles.text}>辅助驾驶数据</div>
                <img className={styles.helpIcon} alt='' id='help-icon'></img>
                {tripsDetail?.zeroTakeOverFlag && <div className={styles.zeroTakeOverTag}>0干预</div>}
              </div>
              <IntelligentDriveData
                intelligentDriveDataArr={tripsDetail.intelligentDriveDataArr}
              />
            </div>
          )
        }
        <div className={styles.driveData}>
          <div className={styles.title}>
            <div className={styles.text}>驾驶数据</div>
          </div>
          <div className={styles.dataFlexBox}>
            <div className={`${styles.dataBlock} ${isSuperErev ? styles.mb10 : styles.smallWidth}`}>
              <div className={styles.data}>{tripsDetail.avgSpeed}</div>
              <div className={styles.name}>{PROP_DICT['avgSpeed']}</div>
            </div>
            <div className={`${styles.dataBlock} ${isSuperErev ? styles.mb10 : styles.smallWidth}`}>
              <div className={styles.data}>{tripsDetail.maxSpeed}</div>
              <div className={styles.name}>{PROP_DICT['maxSpeed']}</div>
            </div>
            <div className={`${styles.dataBlock} ${isSuperErev ? '': styles.smallWidth}`}>
              <div className={styles.data}>{tripsDetail.avgEnergyPowerByDistance}</div>
              {
                isSuperErev ? 
                <div className={styles.name}>{PROP_DICT['avgEnergyElectricByDistance']}</div> : 
                <div className={styles.name}>{PROP_DICT['avgEnergyPowerByDistance']}</div>
              }
              {/* <div className={styles.name}>{PROP_DICT['avgEnergyPowerByDistance']}</div> */}
            </div>
            {
              isSuperErev &&
                <div className={`${styles.dataBlock} ${isSuperErev ? '': styles.smallWidth}`}>
                  <div className={styles.data}>{tripsDetail.avgFuelConsumptionByDistance}</div>
                  <div className={styles.name}>{PROP_DICT['avgFuelConsumptionByDistance']}</div>
                </div>
            }
          </div>
        </div>
        <div className={`${styles.userBar} ${showScreenshot ? styles.showUserBar : ''}`}>
          <img className={styles.headImg} alt='' src={loginInfo.avatar} crossOrigin='anonymous'></img>
          <div className={styles.info}>
            <div className={styles.nickname}>{loginInfo.nickname}</div>
            <div className={styles.mileageInfo}>
              已成为{vehicleInfo.vehicleTypeSaleName}车主{daysToBeCarOwner}天<br />
              总里程{mileageInfo.totalOdometer}km，辅助驾驶里程{mileageInfo.totalAdMileage}km
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App